import { useEffect, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Search, ArrowRight } from 'lucide-react';

interface Action {
  id: string;
  label: string;
  hint?: string;
  icon?: ReactNode;
  run: () => void;
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!open) setQ('');
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const actions: Action[] = [
    { id: 'go-overview',  label: 'Go to Overview',     run: () => { navigate('/admin'); onClose(); } },
    { id: 'go-scrapes',   label: 'Go to Scrape & URLs', run: () => { navigate('/admin/scrapes'); onClose(); } },
    { id: 'go-catalogue', label: 'Go to Catalogue',    run: () => { navigate('/admin/catalogue'); onClose(); } },
    { id: 'go-orders',    label: 'Go to Orders',       run: () => { navigate('/admin/orders'); onClose(); } },
    
    { id: 'go-users',     label: 'Go to Users',        run: () => { navigate('/admin/users'); onClose(); } },
    { id: 'act-sync',     label: 'Trigger sync now',   hint: 'S', run: async () => {
        try { await supabase.functions.invoke('api-sync-prices'); } catch {}
        onClose();
      } },
    { id: 'act-match',    label: 'Run basket match',   hint: 'M', run: async () => {
        try { await supabase.functions.invoke('api-trigger-basket-match'); } catch {}
        onClose();
      } },
  ];

  const filtered = q.trim()
    ? actions.filter(a => a.label.toLowerCase().includes(q.toLowerCase()))
    : actions;

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-start justify-center pt-32"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
        style={{ background: '#0F1A33' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-white/5">
          <Search className="h-4 w-4 text-slate-500" />
          <input
            autoFocus
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search actions, jump to section…"
            className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-slate-500"
          />
          <kbd className="text-[10px] px-1.5 py-0.5 rounded border border-white/10 text-slate-500">ESC</kbd>
        </div>
        <ul className="max-h-80 overflow-auto py-1">
          {filtered.map(a => (
            <li key={a.id}>
              <button
                onClick={a.run}
                className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-white/[0.04] transition group"
              >
                <span className="flex items-center gap-3 text-sm text-slate-200">
                  <ArrowRight className="h-3.5 w-3.5 text-slate-500 group-hover:text-white transition" />
                  {a.label}
                </span>
                {a.hint && (
                  <kbd className="text-[10px] px-1.5 py-0.5 rounded border border-white/10 text-slate-500 admin-mono">{a.hint}</kbd>
                )}
              </button>
            </li>
          ))}
          {filtered.length === 0 && (
            <li className="px-4 py-6 text-center text-sm text-slate-500">No matches.</li>
          )}
        </ul>
      </div>
    </div>
  );
}
